import { useRef, useState } from 'react'

import { Code, ConnectError } from '@connectrpc/connect'
import type { CheckBilateralTradeCsvResponse } from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_service_pb'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  Alert,
  Box,
  Checkbox,
  PrimaryButton,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TableScrollArea,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { CsvDiffPanel } from '~/components/common/CsvUpload/CsvDiffPanel'
import { CsvUploadFileInfo } from '~/components/common/CsvUpload/CsvUploadFileInfo'
import { CsvUploadNoopBanner } from '~/components/common/CsvUpload/CsvUploadNoopBanner'
import { CsvUploadPanelFooter } from '~/components/common/CsvUpload/CsvUploadPanel'
import { CsvUploadStep1Panel } from '~/components/common/CsvUpload/CsvUploadStep1Panel'
import { Dropzone } from '~/components/common/CsvUpload/Dropzone'
import { Stepper } from '~/components/common/CsvUpload/Stepper'
import { Modal } from '~/components/common/Modal'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { useCsvImportReturn } from '~/hooks/useCsvImportReturn'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { downloadCsv } from '~/libs/downloadCsv'
import { appToaster } from '~/libs/toaster'
import {
  type BilateralTradeCsvRow,
  bilateralTradeCsvColumnGuide,
  bilateralTradeCsvSampleRow,
  bilateralTradeCsvTemplateFileName,
  bilateralTradeCsvTemplateRows,
  parseBilateralTradeCsv,
  serializeBilateralTradeCsvRows,
  shouldWarnBilateralTradeCsvDelete,
} from '~/models/bilateralTradeCsv'
import { now as serverNow } from '~/models/clock'
import { decodeCsvFile } from '~/models/csvRecords'
import { resolveCsvUploadConfirmGate } from '~/models/csvUploadConfirmGate'
import { formatJstTimeHm } from '~/models/dateTime'
import {
  STATUS_BADGE_LABEL_KEYS,
  type StatusBadgeVariant,
  deriveStatusBadge,
} from '~/models/planSubmission/statusBadge'
import {
  FilterCards,
  type PreviewFilter,
} from '~/pages/BilateralTradeCsvUpload/components/FilterCards'
import { PreviewTable } from '~/pages/BilateralTradeCsvUpload/components/PreviewTable'
import {
  useApplyBilateralTradeCsv,
  useCheckBilateralTradeCsv,
} from '~/pages/BilateralTradeCsvUpload/hooks/useImportBilateralTrades'
import { routePaths } from '~/routes/routePaths'

type FileInfo = {
  name: string
  size: number
  selectedAt: Date
}

/** バイト数を "38 KB" のような概算表示に変換する。 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

/**
 * 相対取引契約 CSV アップロード画面 (①契約マスタ CSV の唯一の取込導線)。
 *
 * システム管理 > CSVアップロード 配下の 2 ステップウィザード
 * (Step1 ファイル準備・選択 → Step2 差分プレビュー → 確定ダイアログ)。
 * 2026-09-01 の PdM 再確認 (issue #5123) で「テンプレート準備は独立ステップに
 * せず Step1 パネル内に統合する」方針が確定し、旧 Step0 を廃止した
 * (issue #6990。CIS契約マスタ側の同種対応は issue #6467 → PR #7087)。
 * ステップインジケータは他 2 画面 (`Supply48DataCsvUpload` /
 * `CisContractCsvUpload`) と同じ共有 `~/components/common/CsvUpload/Stepper`
 * (2 ステップ固定) を使う。
 *
 * 取込は共通基盤方式の 2 段 API (設計 bilateral-manual-entry-mock-port §4
 * 「CSV 基盤」):
 *
 *   1. CheckBilateralTradeCsv — 行ステータス 5 種 (変更なし / 新規 / 変更 /
 *      削除 / エラー) の差分プレビューと引換券を受け取る。永続的な副作用なし
 *   2. ApplyBilateralTradeCsv — 引換券で確定する。**エラー行が 1 件でもあれば
 *      backend が全体を拒否する** ので、画面も確定ボタンを閉じる
 *
 * 誤削除防止 4 点セット (spec AC-DUB-028〜031) のうち画面側は
 * 「100 件以上 または 既存取引の 50% 超 の警告バナー」と「確定ダイアログの
 * 確認チェックボックス」を担う (削除上限 1,000 件と削除対象不在の検出は
 * backend)。比率の分母は backend が Check レスポンスの deletable_trade_count
 * で渡し、発火判定は models の shouldWarnBilateralTradeCsvDelete が持つ。
 *
 * Step ごとの UI は page-local component に分割している (`components/`):
 * `FilterCards` + `PreviewTable` (Step2 差分プレビュー)。Step1 パネル全体
 * (見出し `1. CSV ファイルを準備` 〜 `2. 準備したファイルを選択`) は共有
 * `~/components/common/CsvUpload/CsvUploadStep1Panel` が節構成を所有し、
 * dropzone・エラー表示等の選択段の中身だけをこの画面が children として渡す
 * (2 画面目の供給地点マスタが現れたため共通コンポーネントへ引き上げた。
 * issue #6467 → #7218)。leg 概念の廃止 (issue #6131) により、スプレッド束ね
 * 表示 (旧 `SpreadGroupSummary`) は撤去した — 本 CSV は 1 行 = 1 取引ID の
 * 独立契約で、行同士を束ねて表示する概念自体が無くなったため。
 */
export const BilateralTradeCsvUploadPage = (): React.ReactElement => {
  const showSuccessToast = useSuccessToast()
  const { t, i18n } = useTranslation()
  const isEnglish = i18n.language.startsWith('en')
  const checkMutation = useCheckBilateralTradeCsv()
  const applyMutation = useApplyBilateralTradeCsv()
  const csvReturn = useCsvImportReturn('bilateralTrade')
  const navigate = useNavigate()

  const [step, setStep] = useState<1 | 2>(1)
  const [showDeliveryPlanDetails, setShowDeliveryPlanDetails] = useState(false)
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null)
  const [parsedRows, setParsedRows] = useState<BilateralTradeCsvRow[]>([])
  const [parseError, setParseError] = useState<string | undefined>(undefined)
  // チェック RPC の結果 (= 差分プレビューの SoT)。行ステータス 5 種・件数・
  // スプレッド束ね明細はすべて backend が決めるため、proto message をそのまま
  // 保持する (frontend で個別 field へ詰め替えない)。
  const [checkResult, setCheckResult] =
    useState<CheckBilateralTradeCsvResponse | null>(null)
  const [serverErrorMessage, setServerErrorMessage] = useState<
    string | undefined
  >(undefined)
  // 削除行を含む CSV の確定を、削除の権限が無くて拒否された状態 (ApplyCsv の PermissionDenied)。
  // サーバーの文言 (rawMessage、 en でも日本語) ではなくエラーコードで文言を決める。
  const [isDeleteForbidden, setIsDeleteForbidden] = useState(false)
  const clearServerError = (): void => {
    setServerErrorMessage(undefined)
    setIsDeleteForbidden(false)
  }
  const [confirmErrorMessage, setConfirmErrorMessage] = useState<
    string | undefined
  >(undefined)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false)
  const [currentFilter, setCurrentFilter] = useState<PreviewFilter>('all')
  // FileReader は非同期のため、読込中に別ファイルを選び直すと古い読込結果が
  // 後着して最新選択を上書きしうる。連番で最新の読込だけを state に反映する。
  const readSeqRef = useRef(0)
  // 確定中にファイル選択へ戻った場合、後着応答で新しいCSVを上書きしない。
  const previewGenerationRef = useRef(0)
  // 確定 RPC へ渡す生バイト列の元テキスト。CSV は backend が parse するため、
  // frontend は読み込んだ本文をそのまま送る (= 画面側で正規化して意味を変えない)。
  const fileBytesRef = useRef<Uint8Array>(new Uint8Array())

  const hasErrorRows = checkResult !== null && checkResult.errorCount > 0
  const deletedCount = checkResult?.deletedCount ?? 0
  const { canOpenConfirm, showNoRowsToProcessBanner } =
    resolveCsvUploadConfirmGate(
      checkResult === null
        ? undefined
        : {
            newRowCount: checkResult.createdCount,
            changedRowCount: checkResult.updatedCount,
            deletedRowCount: checkResult.deletedCount,
          },
      hasErrorRows,
    )

  const resetToStep1 = (): void => {
    previewGenerationRef.current += 1
    setStep(1)
    setFileInfo(null)
    setParsedRows([])
    setParseError(undefined)
    setCheckResult(null)
    setShowDeliveryPlanDetails(false)
    clearServerError()
    setConfirmErrorMessage(undefined)
    setDeleteAcknowledged(false)
    setCurrentFilter('all')
    fileBytesRef.current = new Uint8Array()
  }

  const handleFileSelected = (file: File): void => {
    previewGenerationRef.current += 1
    clearServerError()
    setConfirmErrorMessage(undefined)
    setCheckResult(null)
    setShowDeliveryPlanDetails(false)
    setParseError(undefined)
    setParsedRows([])
    // 読込完了までは旧ファイルの本文・選択情報を残さない。残すと読込中に旧内容の
    // まま確定へ進めてしまい、「選び直したのは新ファイル・送信は旧ファイル」の
    // 不一致が起きる。
    setFileInfo(null)
    fileBytesRef.current = new Uint8Array()
    const seq = ++readSeqRef.current
    // common.md §1「文字コードの判定」: UTF-8 → Shift_JIS の順で判定し、判定した文字コードで
    // 読んだ値で画面の検証を行う。サーバーへは元のバイト列を送る (サーバーも同じ順で判定する)。
    // どちらでも読めない時は、文字化けしたヘッダー不一致ではなく §4 の文字コードのエラーにする。
    decodeCsvFile(file)
      .then((decoded) => {
        if (seq !== readSeqRef.current) return
        if (decoded.kind === 'read') {
          setServerErrorMessage(t('common:upload.error.generic'))
          return
        }
        if (decoded.kind === 'encoding') {
          setParseError(t('common:upload.error.notUtf8'))
          return
        }
        const parsed = parseBilateralTradeCsv(decoded.text, isEnglish)
        fileBytesRef.current = decoded.bytes
        setParsedRows(parsed.rows)
        setParseError(
          parsed.quoteErrorRow !== undefined
            ? t('common:upload.error.quoteInvalid', {
                row: parsed.quoteErrorRow,
              })
            : (parsed.error ??
                // ヘッダー行だけ・全行が空のファイルは Step1 で止める (AC-DUC-062)。
                (parsed.rows.length === 0
                  ? t('common:upload.error.noDataRows')
                  : undefined)),
        )
        setFileInfo({
          name: file.name,
          size: file.size,
          selectedAt: serverNow(),
        })
      })
      .catch(() => {
        if (seq !== readSeqRef.current) return
        // 読込・チェックの失敗は確定時と別の定型文 (common.md §4)。
        setServerErrorMessage(t('common:upload.error.generic'))
      })
  }

  /** Step1: 空テンプレートではなく、記入例 4 行 (① 定型 / ② 非定型 / ③ 期間分割 / ④ スプレッド) 付きの雛形を DL する。 */
  const handleTemplateDownload = (): void => {
    downloadCsv(
      bilateralTradeCsvTemplateFileName(isEnglish),
      serializeBilateralTradeCsvRows(
        bilateralTradeCsvTemplateRows(isEnglish),
        isEnglish,
      ),
    )
  }

  /** Step1 → Step2: チェック RPC を呼んで差分プレビューを取得する。 */
  const handleCheck = async (): Promise<void> => {
    clearServerError()
    try {
      const checked = await checkMutation.mutateAsync({
        csvContent: fileBytesRef.current,
        // csv_upload_history.csv_file_name に記録される
        // (ADR csv-upload-history-best-effort-recording.md)。
        csvFileName: fileInfo?.name,
      })
      setCheckResult(checked)
      setDeleteAcknowledged(false)
      setCurrentFilter('all')
      setStep(2)
    } catch (err) {
      // `message` は "[invalid_argument] …" とコードが前置されるため、サーバーの文言どおり出す
      // `rawMessage` を使う (AC-DUC-045: 画面側とサーバー側で同じ文言)。
      setServerErrorMessage(
        err instanceof ConnectError
          ? err.rawMessage
          : t('common:upload.error.generic'),
      )
    }
  }

  /**
   * Step2 → 確定: 引換券を渡して差分 upsert を実行する。
   *
   * AC-S4G-006 / AC-SBL-045 (「実行中」→「完了」の 2 段階フィードバック):
   * 確定ボタンの isLoading (スピナー) だけでは何が起きているか伝わらないため、
   * 48 点データ再計算が進行中であることを文言で示す info toast を追加する
   * (design `admin/uploads/bilateral-trade.html:773` 準拠)。ただし同 design の
   * 「進捗は CSV 入出力画面で確認できます」は非同期実行を前提にした文言で、
   * 実装は保存と同一トランザクション内の同期実行 (`bilateral_trade_derive48.go`
   * に goroutine/queue 無し) のため、存在しない進捗確認手段は示唆しない。
   * toast は明示的に remove し、既定の自動消灯 (5000ms。成功/エラー toast より
   * 先に消えて「何も起きていない」ように見える) に依存しない。`duration: Infinity`
   * を明示して zag-js の toast machine に自動消灯を止めさせる (`persist` 判定、
   * @zag-js/toast の toast.machine)。
   * `type: 'loading'` は `AppToaster` の型上は許容されるが、実 DS 実装のアイコン
   * map (`Pr`) に `loading` キーが無く icon component が `undefined` になって
   * クラッシュするため使わない (`info` を使う。libs/toaster.ts の型定義は現状
   * ランタイムと不一致だが、本 PR の scope 外として型修正はしない)。
   */
  const handleCommit = async (): Promise<void> => {
    if (checkResult === null || !canOpenConfirm) return
    const generation = previewGenerationRef.current
    clearServerError()
    setConfirmErrorMessage(undefined)
    const processingToastId = appToaster.create({
      type: 'info',
      title: t(
        'page:bilateralTradeCsvUpload.confirmModal.processingToastTitle',
      ),
      description: t(
        'page:bilateralTradeCsvUpload.confirmModal.processingToastDesc',
      ),
      duration: Infinity,
      // Apply RPC が返らないと `finally` の remove に届かないため閉じる口を残す (#8114)
      closable: true,
    })
    try {
      await applyMutation.mutateAsync({ checkToken: checkResult.checkToken })
      // 「完了」 のトーストは 48点データへの反映まで伝える (AC-S4G-006 の 2 段階フィードバック)
      showSuccessToast({
        title: t('page:bilateralTradeCsvUpload.successToast'),
        description: t('page:bilateralTradeCsvUpload.successToastDesc'),
      })
      if (generation === previewGenerationRef.current) {
        csvReturn.returnAfterApply({
          created: checkResult.createdCount,
          updated: checkResult.updatedCount,
          deleted: checkResult.deletedCount,
        })
        setShowConfirmModal(false)
      }
    } catch (err) {
      if (generation !== previewGenerationRef.current) return
      const message =
        err instanceof ConnectError
          ? err.rawMessage
          : t('page:bilateralTradeCsvUpload.serverError')
      // check_token の失効、確定時再検証、競合・権限・入力拒否は、同じ内容を
      // 再送しても解消しない。ApplyCsv が返すこれらの業務コードは Step2 に戻し、
      // その他の通信・サーバー失敗はモーダル内に残して再試行させる。
      if (
        err instanceof ConnectError &&
        (err.code === Code.NotFound ||
          err.code === Code.AlreadyExists ||
          err.code === Code.FailedPrecondition ||
          err.code === Code.Aborted ||
          err.code === Code.PermissionDenied ||
          err.code === Code.InvalidArgument)
      ) {
        // 削除行を含む確定の PermissionDenied は、再送しても通らない削除権限の拒否。
        // サーバー文言を出さず、コードから決めた 1 本のエラーにする。
        if (err.code === Code.PermissionDenied && deletedCount > 0) {
          setIsDeleteForbidden(true)
        } else {
          setServerErrorMessage(message)
        }
        setShowConfirmModal(false)
      } else {
        setConfirmErrorMessage(message)
      }
    } finally {
      appToaster.remove(processingToastId)
    }
  }

  // AC-DUB-030 の判定 (2 条件 OR・境界の非対称・分母 0 の扱い) は models の
  // 純関数が持つ。分母は backend が数えた deletable_trade_count をそのまま使う。
  const showDeleteWarning = shouldWarnBilateralTradeCsvDelete(
    deletedCount,
    checkResult?.deletableTradeCount ?? 0,
  )
  // AC-DUB-031: 削除を含む確定はチェックボックス必須。
  const commitBlockedByDeleteAck = deletedCount > 0 && !deleteAcknowledged

  // AC-DUB-060: delete=1 の物理受渡取引のうち、OCCTO へ提出した（または提出
  // 処理中の）計画に含まれる受渡日を持つ件数。判定・除外 (ゲートクローズ済み
  // 受渡日) はすべて backend (deliveryPlanWarningTradeIDs) が行い、frontend は
  // 件数の有無だけを見る。
  const deliveryPlanWarningCount = checkResult?.deliveryPlanWarningCount ?? 0
  const showDeliveryPlanWarning = deliveryPlanWarningCount > 0
  const deliveryPlanWarnings = checkResult?.deliveryPlanWarnings ?? []
  const statusOrder: StatusBadgeVariant[] = [
    'generating',
    'unsubmitted',
    'approvalPending',
    'submitting',
    'submitted',
    'accepted',
    'rejected',
    'inconsistent',
    'creationFailed',
    'submitFailed',
    'sendFailed',
  ]

  // アップロード済みファイル情報 (ファイル名 / サイズ / 選択時刻)。 common.md
  // 「アップロード済みファイル情報の表示 (Step2 / Step3 共通)」に従い Step1 /
  // Step2 の両方に常時表示する。
  const fileInfoBox = fileInfo !== null && (
    <CsvUploadFileInfo
      meta={`${formatFileSize(fileInfo.size)} ・ ${t(
        'page:bilateralTradeCsvUpload.fileInfo.selectedAt',
        { time: formatJstTimeHm(fileInfo.selectedAt) },
      )}`}
      name={fileInfo.name}
    />
  )

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('page:bilateralTradeCsvUpload.breadcrumb.admin'),
          to: routePaths.adminHub.path,
        },
        {
          label: t('common:route.adminUploads'),
          to: routePaths.adminUploads.path,
        },
        {
          label: t('page:bilateralTradeCsvUpload.breadcrumb.title'),
          bold: true,
        },
      ]}
    >
      {/* spec `admin/data/uploads/index.md:126` :
          「個別アップロード画面のタイトル下補足も本表の説明文と一致させる。 文言変更は
           本 spec の更新をもって行い、 **実装・プロトタイプ側で独自の説明文を持たない**」。
          そのため取込ハブのカードと**同じ i18n キーを共有**する (別キーに複製すると
          片方だけ直って drift する = issue #4325 で禁じられた状態に戻る)。
          見出しは渡さない — パンくず末尾 (bold) が画面名を兼ねる (PageIntro の docstring)。 */}
      <PageIntro
        description={t('page:adminUploads.types.bilateralTrade.desc')}
      />
      {/* 外周 padding は `PageLayout` が包む DS の `SideMenuLayoutContentLayout`
          (`padding="4"` = 16px) が持つ。 ここで更に足すと本画面だけ上・左・右が
          32px になり他画面と揃わない (design `css/layout.css` の
          `.content { padding: 16px }` が正。 デザイナー指摘 2026-08-28)。

          説明文との縦間隔だけは自前で持つ。 `PageIntro` は外側 margin を持たず
          (縦 spacing の所有権を置き場所側へ一本化する方針)、 `PageLayout` も
          children 間に gap を与えないため、 撤去した padding が担っていた 16px が
          消えてステッパーが説明文に接する。 design は説明文側に
          `margin-bottom: var(--space-4)` を置いている (レビュー指摘 2026-08-28)。 */}
      <Box marginTop={4}>
        <Stepper
          currentStep={step}
          stepLabels={[
            t('page:bilateralTradeCsvUpload.stepper.step1'),
            t('page:bilateralTradeCsvUpload.stepper.step2'),
          ]}
        />

        {step === 1 && (
          <CsvUploadStep1Panel
            downloads={[{ onClick: handleTemplateDownload }]}
            framed
            guideSections={[{ rows: bilateralTradeCsvColumnGuide(isEnglish) }]}
            sampleRow={bilateralTradeCsvSampleRow(isEnglish)}
            selectHint={t('page:bilateralTradeCsvUpload.step1.hint')}
          >
            <Dropzone
              onFileSelected={handleFileSelected}
              specText={t('page:bilateralTradeCsvUpload.step1.spec')}
            />

            {parseError !== undefined && (
              <CompactAlert
                description={parseError}
                status="error"
                title={t('page:bilateralTradeCsvUpload.step1.syntaxErrorTitle')}
              />
            )}

            {serverErrorMessage !== undefined && (
              <CompactAlert status="error" title={serverErrorMessage} />
            )}

            {/* 仕様の行との間は design `.up-file-info` の margin-top 16px (gap 12px + 4px) */}
            {fileInfo !== null && <Box marginTop={1}>{fileInfoBox}</Box>}

            <CsvUploadPanelFooter>
              {/* design (`admin/uploads/*.html` `#btn-cancel-step1`) どおり全種別に置く。
                    行き先は取込を始めた画面 (returnTo)、 無ければ CSV 入出力の一覧 */}
              <TertiaryButton
                onClick={() => {
                  void navigate(csvReturn.cancelPath)
                }}
                size="md"
              >
                {t('common:action.cancel')}
              </TertiaryButton>
              {fileInfo !== null &&
                parseError === undefined &&
                parsedRows.length > 0 && (
                  <PrimaryButton
                    disabled={checkMutation.isPending}
                    onClick={() => {
                      void handleCheck()
                    }}
                    size="md"
                  >
                    {t('page:bilateralTradeCsvUpload.step1.toStep2')}
                  </PrimaryButton>
                )}
            </CsvUploadPanelFooter>
          </CsvUploadStep1Panel>
        )}

        {step === 2 && checkResult !== null && (
          <CsvDiffPanel
            back={{
              label: t('page:bilateralTradeCsvUpload.step2.backToStep1'),
              onClick: resetToStep1,
              disabled: csvReturn.isReturning,
            }}
            confirm={{
              label: t('page:bilateralTradeCsvUpload.step2.confirmOpen'),
              onClick: () => setShowConfirmModal(true),
              // 業務画面へ戻るのを待つ間に、 同じ CSV をもう一度確定させない
              // 削除権限が無いと拒否された CSV は、確定を押しても同じ拒否になるだけなので
              // 押せなくする。理由は直上のエラー Alert が文で示す (errorRows と同じ作り)。
              disabled:
                !canOpenConfirm || isDeleteForbidden || csvReturn.isReturning,
            }}
            hint={t('page:bilateralTradeCsvUpload.step2.hint')}
            title={t('page:bilateralTradeCsvUpload.step2.title')}
          >
            {fileInfo !== null && <Box marginBottom={3}>{fileInfoBox}</Box>}

            {hasErrorRows && (
              <Box marginBottom={3}>
                <CompactAlert
                  status="error"
                  title={t(
                    'page:bilateralTradeCsvUpload.step2.errorRowsBlock',
                    {
                      count: checkResult.errorCount,
                    },
                  )}
                />
              </Box>
            )}

            {showNoRowsToProcessBanner && (
              <CsvUploadNoopBanner
                description={t(
                  'page:bilateralTradeCsvUpload.step2.noopBannerDesc',
                  { count: checkResult.unchangedCount },
                )}
              />
            )}

            {showDeleteWarning && !isDeleteForbidden && (
              <Box marginBottom={3}>
                <CompactAlert
                  status="warning"
                  title={t('page:bilateralTradeCsvUpload.step2.deleteWarning', {
                    count: deletedCount,
                    total: checkResult?.deletableTradeCount ?? 0,
                  })}
                />
              </Box>
            )}

            {isDeleteForbidden && (
              <Box marginBottom={3}>
                <CompactAlert
                  status="error"
                  // 「相対取引の削除」は仮の権限名 (dev にはまだ権限モデルが無い)。
                  // 権限モデルが決まったら、ここと i18n の文言の権限名をそろえて直す。
                  title={t(
                    'page:bilateralTradeCsvUpload.step2.deleteForbiddenError',
                    { count: deletedCount },
                  )}
                />
              </Box>
            )}

            {/* AC-DUB-060: 見出し + 本文の 2 段構成 (spec 文言をそのまま使う)。
                confirmModal の recalcWarning と同じ title/description 分割
                (`.claude/rules/frontend.md` — Alert の書式は component に任せ、
                自前で Box + Text を組まない)。 */}
            {showDeliveryPlanWarning && (
              <Box marginBottom={3}>
                <CompactAlert
                  actions={
                    deliveryPlanWarnings.length > 0 ? (
                      <TertiaryButton
                        onClick={() => setShowDeliveryPlanDetails(true)}
                        size="sm"
                      >
                        {t(
                          'page:bilateralTradeCsvUpload.step2.deliveryPlanDetailsButton',
                        )}
                      </TertiaryButton>
                    ) : undefined
                  }
                  description={t(
                    'page:bilateralTradeCsvUpload.step2.deliveryPlanWarningDesc',
                    { count: deliveryPlanWarningCount },
                  )}
                  status="warning"
                  title={t(
                    'page:bilateralTradeCsvUpload.step2.deliveryPlanWarningTitle',
                  )}
                />
              </Box>
            )}

            <Modal
              footer={
                <TertiaryButton
                  onClick={() => setShowDeliveryPlanDetails(false)}
                  size="md"
                >
                  {t('common:action.close')}
                </TertiaryButton>
              }
              isOpen={
                showDeliveryPlanDetails &&
                showDeliveryPlanWarning &&
                deliveryPlanWarnings.length > 0
              }
              onClose={() => setShowDeliveryPlanDetails(false)}
              size="md"
              title={t(
                'page:bilateralTradeCsvUpload.step2.deliveryPlanDetailsTitle',
              )}
            >
              <Text>
                {t(
                  'page:bilateralTradeCsvUpload.step2.deliveryPlanDetailsCount',
                  { count: deliveryPlanWarnings.length },
                )}
              </Text>
              <TableScrollArea>
                <TableRoot size="sm">
                  <TableHeader>
                    <TableRow>
                      <TableColumnHeader>
                        {t(
                          'page:bilateralTradeCsvUpload.step2.columns.publicTradeId',
                        )}
                      </TableColumnHeader>
                      <TableColumnHeader>
                        {t(
                          'page:bilateralTradeCsvUpload.step2.deliveryPlanDetailsDate',
                        )}
                      </TableColumnHeader>
                      <TableColumnHeader>
                        {t(
                          'page:bilateralTradeCsvUpload.step2.deliveryPlanDetailsStatus',
                        )}
                      </TableColumnHeader>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deliveryPlanWarnings.map((warning) => {
                      const d = warning.deliveryDate
                      const labels = [
                        ...new Set(
                          warning.statuses.map(
                            (status) => deriveStatusBadge(status).variant,
                          ),
                        ),
                      ]
                        .sort(
                          (a, b) =>
                            statusOrder.indexOf(a) - statusOrder.indexOf(b),
                        )
                        .map((variant) => t(STATUS_BADGE_LABEL_KEYS[variant]))
                      return (
                        <TableRow
                          key={`${warning.tradeId}-${d?.year}-${d?.month}-${d?.day}`}
                        >
                          <TableCell>{warning.tradeId}</TableCell>
                          <TableCell>
                            {d === undefined
                              ? ''
                              : `${d.year}/${String(d.month).padStart(2, '0')}/${String(d.day).padStart(2, '0')}`}
                          </TableCell>
                          <TableCell>
                            {labels.join(isEnglish ? ', ' : '、')}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </TableRoot>
              </TableScrollArea>
            </Modal>

            {serverErrorMessage !== undefined && (
              <Box marginBottom={3}>
                <CompactAlert status="error" title={serverErrorMessage} />
              </Box>
            )}

            <FilterCards
              activeFilter={currentFilter}
              checkResult={checkResult}
              onFilterChange={setCurrentFilter}
            />

            <PreviewTable
              filter={currentFilter}
              parsedRows={parsedRows}
              rows={checkResult.rows}
            />
          </CsvDiffPanel>
        )}

        <ConfirmDialog
          body={
            <Box>
              {confirmErrorMessage !== undefined && (
                <Alert
                  marginBottom={3}
                  status="error"
                  title={confirmErrorMessage}
                />
              )}
              <Text marginBottom={2}>
                {t('page:bilateralTradeCsvUpload.confirmModal.body')}
              </Text>
              {fileInfo !== null && (
                <Text color="Text.midEmphasis" fontSize="sm">
                  {fileInfo.name}
                </Text>
              )}
              <Text fontSize="sm">
                {t('page:bilateralTradeCsvUpload.confirmModal.counts', {
                  created: checkResult?.createdCount ?? 0,
                  updated: checkResult?.updatedCount ?? 0,
                  deleted: deletedCount,
                })}
              </Text>
              {/* AC-DUB-060 該当時のみ AC-DUB-031 の削除件数文言に続けて併記する。 */}
              {showDeliveryPlanWarning && (
                <Text fontSize="sm">
                  {t(
                    'page:bilateralTradeCsvUpload.confirmModal.deliveryPlanWarningNote',
                    { count: deliveryPlanWarningCount },
                  )}
                </Text>
              )}
              {/* AC-DUB-025: 確定時に 48 点データが再計算されることを明示する。 */}
              <Alert
                marginTop={3}
                description={t(
                  'page:bilateralTradeCsvUpload.confirmModal.recalcWarningDesc',
                )}
                status="warning"
                title={t(
                  'page:bilateralTradeCsvUpload.confirmModal.recalcWarningTitle',
                )}
              />
              {/* AC-DUB-031: 削除を含む確定は確認チェックボックスを必須にする。 */}
              {deletedCount > 0 && (
                <Box marginTop={3}>
                  <Checkbox
                    checked={deleteAcknowledged}
                    onCheckedChange={(e: { checked: boolean | string }) =>
                      setDeleteAcknowledged(e.checked === true)
                    }
                  >
                    {t(
                      'page:bilateralTradeCsvUpload.confirmModal.deleteAcknowledge',
                      { count: deletedCount },
                    )}
                  </Checkbox>
                </Box>
              )}
            </Box>
          }
          confirmLabel={t('page:bilateralTradeCsvUpload.confirmModal.commit')}
          destructive={deletedCount > 0}
          hideConfirm={commitBlockedByDeleteAck}
          isLoading={applyMutation.isPending}
          isOpen={showConfirmModal}
          onCancel={() => {
            setConfirmErrorMessage(undefined)
            setShowConfirmModal(false)
          }}
          onConfirm={() => {
            void handleCommit()
          }}
          title={t('page:bilateralTradeCsvUpload.confirmModal.title')}
        />
      </Box>
    </PageLayout>
  )
}
